import { axiosClientAdmin } from '@/lib/auth';
import { UseQueryOptions, UseInfiniteQueryOptions } from '@tanstack/react-query';
import {
  MetricBreakdown,
  MetricGranularity,
  MetricMeasure,
  MetricResultDto,
  MetricSortField,
  MetricSortDirection,
} from '@/lib/api/types.gen';
import { getRoundedBrowserTimezone } from '@/lib/timezone';

/**
 * Thrown when CubeJS has queued the query (not yet processed).
 * React Query will automatically retry when it sees this error type.
 */
export class MetricQueuedException extends Error {
  constructor() {
    super('Metric query is queued');
    this.name = 'MetricQueuedException';
  }
}

// Cube/view types - matches backend MetricCube enum
export enum MetricCube {
  MonitoringEvents = 'MonitoringEvents',
  Alerts = 'Alerts',
  Assets = 'Assets',
  AssetDeletions = 'AssetDeletions',  // Uses camera deleted_at as time dimension
  Sites = 'Sites',                     // Uses site created_at as time dimension
  SitesArchived = 'SitesArchived',     // Uses site archived_at as time dimension
  Customers = 'Customers',             // Uses customer created_at as time dimension
  IngestionEvents = 'IngestionEvents', // Ingestion pipeline events (stage/outcome/failure aggregations)
}

// Asset measures
export enum AssetMeasure {
  CameraCount = 'CameraCount',
  CameraWithSpeakerCount = 'CameraWithSpeakerCount',
  SiteCount = 'SiteCount',
  CustomerCount = 'CustomerCount',
  TotalCameraCount = 'TotalCameraCount',
  // Site/Customer creation measures (use Sites/Customers cubes)
  TotalSiteCount = 'TotalSiteCount',
  TotalCustomerCount = 'TotalCustomerCount',
  ArchivedSiteCount = 'ArchivedSiteCount',
  // Camera deletion measures (query with AssetDeletions cube)
  DeletedCameraCount = 'DeletedCameraCount',
  DeletedSiteCount = 'DeletedSiteCount',
  DeletedCustomerCount = 'DeletedCustomerCount',
}

// Asset breakdowns
export enum AssetBreakdown {
  DeviceBrand = 'DeviceBrand',
  HasSpeaker = 'HasSpeaker',
}

// Segments - predefined filters that can be applied to queries
export enum MetricSegment {
  Incidents = 'Incidents',
  PoliceCallouts = 'PoliceCallouts',
  PoliceResponses = 'PoliceResponses',
  SlaBreaches = 'SlaBreaches',
  Closed = 'Closed',
}

// Additional breakdowns for alerts - extends MetricBreakdown from types.gen.ts
// These are defined here until the OpenAPI spec is regenerated
export enum AlertBreakdown {
  AlertStatus = 'AlertStatus',
  OffenceType = 'OffenceType',
}

// Additional measures for monitoring events - extends MetricMeasure from types.gen.ts
// These are defined here until the OpenAPI spec is regenerated
export enum MonitoringEventMeasure {
  BillableEventCount = 'BillableEventCount',
  ActiveCameraCount = 'ActiveCameraCount',  // Distinct cameras with events per time bucket
  ActiveSiteCount = 'ActiveSiteCount',      // Distinct sites with events per time bucket
}

// Ingestion pipeline measures
export enum IngestionMeasure {
  EventCount = 'IngestionEventCount',
  SuccessCount = 'IngestionSuccessCount',
  FailureCount = 'IngestionFailureCount',
  ErrorCount = 'IngestionErrorCount',
  FailureRate = 'IngestionFailureRate',
  UniquePipelineCount = 'IngestionUniquePipelineCount',
  ActiveCameraCount = 'IngestionActiveCameraCount',
  ActiveSiteCount = 'IngestionActiveSiteCount',
}

// Ingestion pipeline breakdowns
export enum IngestionBreakdown {
  Stage = 'IngestionStage',
  Outcome = 'IngestionOutcome',
  Vendor = 'IngestionVendor',
}

// Additional measures for alerts/incidents - extends MetricMeasure from types.gen.ts
// These are defined here until the OpenAPI spec is regenerated
export enum AlertMeasure {
  AlertCount = 'AlertCount',
  IncidentCount = 'IncidentCount',
  SlaBreachCount = 'SlaBreachCount',
  SlaBreachRate = 'SlaBreachRate',
  P50TimeToClose = 'P50TimeToClose',
  P95TimeToClose = 'P95TimeToClose',
  AvgTimeToClose = 'AvgTimeToClose',
  P50TimeToNotify = 'P50TimeToNotify',
  P95TimeToNotify = 'P95TimeToNotify',
  // Police callout measures
  PoliceCalloutCount = 'PoliceCalloutCount',
  PoliceResponseCount = 'PoliceResponseCount',
  PoliceResponseRate = 'PoliceResponseRate',
}

// ============================================================================
// Fetch Metric (Generic)
// ============================================================================

export interface FetchMetricInput {
  tenantId: string;
  cube?: MetricCube;
  measure: MetricMeasure | AlertMeasure | MonitoringEventMeasure | AssetMeasure | IngestionMeasure;
  segments?: MetricSegment[];
  breakdown?: MetricBreakdown | AlertBreakdown | AssetBreakdown | IngestionBreakdown;
  dateStart: Date;
  dateEnd: Date;
  granularity?: MetricGranularity;
  timezone?: string;
  customerId?: string;
  siteId?: string;
  cameraId?: string;
  limit?: number;
  offset?: number;
  sortBy?: MetricSortField;
  sortDirection?: MetricSortDirection;
  includeTotalCount?: boolean;
}

export async function fetchMetric(input: FetchMetricInput): Promise<MetricResultDto> {
  const { tenantId, cube = MetricCube.MonitoringEvents, measure, breakdown, dateStart, dateEnd, granularity, timezone } = input;

  const path = breakdown
    ? `/tenants/${tenantId}/metrics/${cube}/${measure}/${breakdown}`
    : `/tenants/${tenantId}/metrics/${cube}/${measure}`;

  const params: Record<string, string | string[] | undefined> = {
    DateStart: dateStart.toISOString(),
    DateEnd: dateEnd.toISOString(),
    Granularity: granularity,
    Timezone: timezone ?? getRoundedBrowserTimezone(),
    CustomerId: input.customerId,
    SiteId: input.siteId,
    CameraId: input.cameraId,
    Limit: input.limit?.toString(),
    Offset: input.offset?.toString(),
    SortBy: input.sortBy,
    SortDirection: input.sortDirection,
    IncludeTotalCount: input.includeTotalCount?.toString(),
  };

  // Add segments as repeated query params (ASP.NET Core expects Segments=X&Segments=Y format)
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.append(key, value as string);
    }
  }
  if (input.segments) {
    for (const segment of input.segments) {
      searchParams.append('Segments', segment);
    }
  }

  const { data } = await axiosClientAdmin.get<MetricResultDto>(`${path}?${searchParams.toString()}`);

  if (data.meta?.queued) {
    throw new MetricQueuedException();
  }

  return data;
}

export function FetchMetricQuery(input: FetchMetricInput) {
  const timezone = input.timezone ?? getRoundedBrowserTimezone();
  const cube = input.cube ?? MetricCube.MonitoringEvents;

  const finalInput = { ...input, timezone, cube };

  return {
    queryKey: ['metrics', finalInput],
    queryFn: () => fetchMetric(finalInput),
    staleTime: 2 * 60 * 1000, // Cache metrics for 2 minutes - they don't change frequently
    retry: (failureCount: number, error: Error) =>
      error instanceof MetricQueuedException && failureCount < 10,
    retryDelay: (attempt: number) => Math.min(2000 * attempt, 10000),
  } satisfies UseQueryOptions;
}

export function InfiniteFetchMetricQuery(input: FetchMetricInput) {
  const timezone = input.timezone ?? getRoundedBrowserTimezone();
  const cube = input.cube ?? MetricCube.MonitoringEvents;
  const limit = input.limit ?? 10;

  return {
    queryKey: [
      'metrics-infinite',
      input.tenantId,
      cube,
      input.measure,
      input.segments,
      input.breakdown,
      input.dateStart.toISOString(),
      input.dateEnd.toISOString(),
      input.granularity,
      timezone,
      input.customerId,
      input.siteId,
      input.cameraId,
      input.sortBy,
      input.sortDirection,
      limit,
    ],
    queryFn: ({ pageParam }: { pageParam: number }) => {
      return fetchMetric({ ...input, offset: pageParam, limit });
    },
    initialPageParam: 0,
    getNextPageParam: (_lastPage: MetricResultDto, allPages: MetricResultDto[]) => {
      const totalItemsLoaded = allPages.reduce((acc, page) => acc + (page.data?.length ?? 0), 0);
      const totalCount = allPages[0]?.meta?.totalCount ?? 0;

      if (totalItemsLoaded > 0 && totalItemsLoaded < totalCount) {
        return totalItemsLoaded;
      }
      return undefined;
    },
    staleTime: 2 * 60 * 1000,
    retry: (failureCount: number, error: Error) =>
      error instanceof MetricQueuedException && failureCount < 10,
    retryDelay: (attempt: number) => Math.min(2000 * attempt, 10000),
  };
}

// ============================================================================
// Fetch Dimension Values (for getting all unique entities)
// ============================================================================

export interface DimensionValueDto {
  attributes: Record<string, string>;
}

export interface DimensionValuesResultDto {
  meta?: {
    lastRefresh?: string;
    slowQuery?: boolean;
    preAggregated?: boolean;
    totalCount?: number;
    queued?: boolean;
  };
  values: DimensionValueDto[];
}

export interface FetchDimensionValuesInput {
  tenantId: string;
  cube?: MetricCube;
  breakdown: MetricBreakdown;
  customerId?: string;
  siteId?: string;

  /** Optional: constrain dimension values to a reporting window */
  dateStart?: Date;
  dateEnd?: Date;
  /** IANA timezone (e.g. "Pacific/Auckland"); defaults to browser timezone */
  timezone?: string;

  // Asset existence filters (for "assets that existed during period" queries)
  /** Filter to assets created on or before this date */
  createdOnOrBefore?: Date;
  /** Filter to assets not deleted before this date (deleted_at IS NULL OR deleted_at >= value) */
  notDeletedBefore?: Date;

  // Pagination & Sorting
  limit?: number;
  offset?: number;
  includeTotalCount?: boolean;
  /** Field to sort by (e.g., 'name', 'created_at', 'deleted_at') */
  sortField?: string;
  sortDirection?: MetricSortDirection;
}

export async function fetchDimensionValues(
  input: FetchDimensionValuesInput
): Promise<DimensionValuesResultDto> {
  const {
    tenantId,
    cube = MetricCube.MonitoringEvents,
    breakdown,
    customerId,
    siteId,
    dateStart,
    dateEnd,
    timezone,
    createdOnOrBefore,
    notDeletedBefore,
  } = input;
  const path = `/tenants/${tenantId}/metrics/${cube}/dimensions/${breakdown}`;

  const params: Record<string, string> = {};

  if (customerId) params.CustomerId = customerId;
  if (siteId) params.SiteId = siteId;

  params.Timezone = timezone ?? getRoundedBrowserTimezone();

  // Only send a date range if both bounds exist
  if (dateStart && dateEnd) {
    params.DateStart = dateStart.toISOString();
    params.DateEnd = dateEnd.toISOString();
  }

  // Asset existence filters
  if (createdOnOrBefore) {
    params.CreatedOnOrBefore = createdOnOrBefore.toISOString();
  }
  if (notDeletedBefore) {
    params.NotDeletedBefore = notDeletedBefore.toISOString();
  }

  // Pagination & Sorting
  if (input.limit !== undefined) {
    params.Limit = input.limit.toString();
  }
  if (input.offset !== undefined) {
    params.Offset = input.offset.toString();
  }
  if (input.includeTotalCount !== undefined) {
    params.IncludeTotalCount = input.includeTotalCount.toString();
  }
  if (input.sortField) {
    params.SortByDimension = input.sortField;
  }
  if (input.sortDirection) {
    params.SortDirection = input.sortDirection;
  }

  const { data } = await axiosClientAdmin.get<DimensionValuesResultDto>(path, { params });

  if (data.meta?.queued) {
    throw new MetricQueuedException();
  }

  return data;
}

export function FetchDimensionValuesQuery(input: FetchDimensionValuesInput) {
  const cube = input.cube ?? MetricCube.MonitoringEvents;
  const timezone = input.timezone ?? getRoundedBrowserTimezone();
  return {
    queryKey: [
      'dimension-values',
      input.tenantId,
      cube,
      input.breakdown,
      input.customerId,
      input.siteId,
      input.dateStart?.toISOString(),
      input.dateEnd?.toISOString(),
      timezone,
      input.createdOnOrBefore?.toISOString(),
      input.notDeletedBefore?.toISOString(),
    ],
    queryFn: () => fetchDimensionValues(input),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - entity lists don't change often
    retry: (failureCount: number, error: Error) =>
      error instanceof MetricQueuedException && failureCount < 10,
    retryDelay: (attempt: number) => Math.min(2000 * attempt, 10000),
  } satisfies UseQueryOptions;
}

export function InfiniteFetchDimensionValuesQuery(input: FetchDimensionValuesInput) {
  const cube = input.cube ?? MetricCube.MonitoringEvents;
  const timezone = input.timezone ?? getRoundedBrowserTimezone();
  const limit = input.limit ?? 20;

  return {
    queryKey: [
      'dimension-values-infinite',
      input.tenantId,
      cube,
      input.breakdown,
      input.customerId,
      input.siteId,
      input.dateStart?.toISOString(),
      input.dateEnd?.toISOString(),
      timezone,
      input.createdOnOrBefore?.toISOString(),
      input.notDeletedBefore?.toISOString(),
      limit,
      input.sortField,
      input.sortDirection,
    ],
    queryFn: ({ pageParam }: { pageParam: number }) => {
      return fetchDimensionValues({ ...input, offset: pageParam, limit, includeTotalCount: true });
    },
    initialPageParam: 0,
    getNextPageParam: (_lastPage: DimensionValuesResultDto, allPages: DimensionValuesResultDto[]) => {
      const totalItemsLoaded = allPages.reduce((acc, page) => acc + (page.values?.length ?? 0), 0);
      const totalCount = allPages[0]?.meta?.totalCount ?? 0;

      if (totalItemsLoaded > 0 && totalItemsLoaded < totalCount) {
        return totalItemsLoaded;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount: number, error: Error) =>
      error instanceof MetricQueuedException && failureCount < 10,
    retryDelay: (attempt: number) => Math.min(2000 * attempt, 10000),
  };
}
